import { apiClient, unwrap } from './api-client';
import { oneIdentifier } from '~shared/config/hostname';
import type {
  AddOfferCommand,
  BookCommand,
  InquireCommand,
  OneGetOffersCommand,
  SetErvInsuranceOfferCommand,
  SetPromotionCommand,
  UpdateItemCommand,
  UpdateUserCommand,
  UpdateUserCountryCommand,
} from './schemas';
import { Locale } from '~app/i18n';
import {
  DayQueryDto,
  MonthQueryDto,
} from '~features/booking/booking-calendar/model/calendar.types';
import TagManager from 'react-gtm-module';
import { getOfferingById } from '~entities/offering/lib/offering.utils';
import { CartItem } from '~entities/session/model/session.types';

type RequestInit = { signal?: AbortSignal };

export const getConfiguration = (locale: Locale = 'en', init?: RequestInit) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/configuration', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      signal: init?.signal,
    }),
  );

export const getOffers = (
  payload: OneGetOffersCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/offers', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const getSession = (locale: Locale = 'en', init?: RequestInit) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/session', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      signal: init?.signal,
    }),
  );

export const getLastBooking = (locale: Locale = 'en', init?: RequestInit) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/session/last-booking', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      signal: init?.signal,
    }),
  );

export const getBookingDetail = (
  bookingId: string,
  email: string,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.GET('/rest/v1/samera-one/{oneIdentifier}/bookings/{bookingId}', {
      params: {
        path: { oneIdentifier, bookingId },
        query: { email, lang: locale },
      },
      signal: init?.signal,
    }),
  );

// No `lang`: the calendar contract declares none, because neither calendar DTO carries a
// localized string — dates, money, booleans and a stay duration only. That is also why the
// backend's Redis cache for these two can be language-agnostic.
export const getMonth = (payload: MonthQueryDto, init?: RequestInit) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/calendar/month', {
      params: { path: { oneIdentifier } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const getDay = (payload: DayQueryDto, init?: RequestInit) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/calendar/day', {
      params: { path: { oneIdentifier } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const addVariant = (
  variantId: string,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.POST(
      '/rest/v1/samera-one/{oneIdentifier}/session/items/variants',
      {
        params: { path: { oneIdentifier }, query: { lang: locale } },
        body: { variantId },
        signal: init?.signal,
      },
    ),
  );

export const updateUser = (
  payload: UpdateUserCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.PUT('/rest/v1/samera-one/{oneIdentifier}/session/user', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const updateUserCountry = (
  payload: UpdateUserCountryCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.PUT('/rest/v1/samera-one/{oneIdentifier}/session/user/country', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const addToCart = (
  payload: AddOfferCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) => {
  const room = getOfferingById(payload.roomCategoryId);

  TagManager.dataLayer({
    dataLayer: {
      event: 'add_to_cart',
      ecommerce: {
        currency: payload.price.currency,
        value: payload.price.amount,
        items: [
          {
            item_name: room ? room.name : 'Unknown',
            item_id: payload.roomCategoryId,
            price: payload.price.amount,
            quantity: 1,
          },
        ],
      },
    },
  });

  return unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/session/items', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );
};

export const updateCartItem = (
  payload: UpdateItemCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.PUT(
      '/rest/v1/samera-one/{oneIdentifier}/session/items/{itemId}',
      {
        params: {
          path: { oneIdentifier, itemId: payload.id },
          query: { lang: locale },
        },
        body: payload,
        signal: init?.signal,
      },
    ),
  );

export const removeFromCart = (
  cartItem: CartItem,
  locale: Locale = 'en',
  init?: RequestInit,
) => {
  const room = getOfferingById(cartItem.roomId);

  TagManager.dataLayer({
    dataLayer: {
      event: 'remove_from_cart',
      ecommerce: {
        currency: cartItem.price.currency,
        value: cartItem.price.amount,
        items: [
          {
            item_name: room ? room.name : 'Unknown',
            item_id: cartItem.roomId,
          },
        ],
      },
    },
  });

  return unwrap(
    apiClient.DELETE(
      '/rest/v1/samera-one/{oneIdentifier}/session/items/{itemId}',
      {
        params: {
          path: { oneIdentifier, itemId: cartItem.id },
          query: { lang: locale },
        },
        signal: init?.signal,
      },
    ),
  );
};

export const updatePromotion = (
  payload: SetPromotionCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.PUT('/rest/v1/samera-one/{oneIdentifier}/session/promotion', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const updateInsuranceOffer = (
  payload: SetErvInsuranceOfferCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.PUT(
      '/rest/v1/samera-one/{oneIdentifier}/session/erv-insurance-offer',
      {
        params: { path: { oneIdentifier }, query: { lang: locale } },
        body: payload,
        signal: init?.signal,
      },
    ),
  );

export const validateCheckout = (
  payload: BookCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/checkout/validate', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const book = (
  payload: BookCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/checkout/book', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );

export const inquire = (
  payload: InquireCommand,
  locale: Locale = 'en',
  init?: RequestInit,
) =>
  unwrap(
    apiClient.POST('/rest/v1/samera-one/{oneIdentifier}/checkout/inquire', {
      params: { path: { oneIdentifier }, query: { lang: locale } },
      body: payload,
      signal: init?.signal,
    }),
  );
